import { useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'

// Keywords that get the highlighted gold-chip treatment
const HIGHLIGHT_RE = /description|specification|technical|feature|benefit|packag|certificate|quality|application|ingredient|nutrition|storage|shelf/i

const isTableLine = (l) => /^\|.*\|$/.test(l.trim())
const isDivider = (l) => /^[—–\-_\s·]{5,}$/.test(l.trim())
const isHeadingLine = (l) => {
  const s = l.trim()
  return s.length > 0 && s.length < 80 && !/[.,;!?]$/.test(s)
}

function parseTableRows(lines) {
  const rows = lines.map((l) =>
    l.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim())
  ).filter((cells) => cells.length > 0 && !cells.every((c) => /^:?-+:?$/.test(c)))
  if (rows.length < 2) return null
  const header = rows[0]
  // drop body rows that just repeat the header (common in scraped data)
  const body = rows.slice(1).filter((r) => r.join('|') !== header.join('|'))
  return { header, body }
}

/**
 * Splits a raw backend description into renderable blocks:
 * headings, paragraphs, markdown tables and dividers.
 */
export function parseDescription(raw) {
  const text = (raw || '').replace(/\u00a0/g, ' ').replace(/\r/g, '')
  const groups = []
  let cur = []
  for (const line of text.split('\n')) {
    if (!line.trim()) {
      if (cur.length) { groups.push(cur); cur = [] }
    } else {
      cur.push(line.trim())
    }
  }
  if (cur.length) groups.push(cur)

  const blocks = []
  for (const g of groups) {
    if (g.every(isTableLine)) {
      const table = parseTableRows(g)
      if (table) { blocks.push({ type: 'table', table }); continue }
    }
    if (g.every(isDivider)) { blocks.push({ type: 'hr' }); continue }
    if (g.length === 1 && isHeadingLine(g[0])) {
      blocks.push({ type: 'heading', text: g[0].replace(/:$/, ''), highlight: HIGHLIGHT_RE.test(g[0]) })
      continue
    }
    // Label line like "Specifications:" followed directly by body text —
    // split the label out so it still gets the heading treatment
    if (g.length > 1 && g[0].endsWith(':') && g[0].length < 80) {
      blocks.push({ type: 'heading', text: g[0].replace(/:$/, ''), highlight: HIGHLIGHT_RE.test(g[0]) })
      blocks.push({ type: 'paragraph', text: g.slice(1).join(' ') })
      continue
    }
    blocks.push({ type: 'paragraph', text: g.join(' ') })
  }

  // Drop a leading bare "Description" heading — the section provides its own title
  if (blocks[0]?.type === 'heading' && /^description$/i.test(blocks[0].text)) blocks.shift()
  return blocks
}

/** First prose paragraph, trimmed for the buy-column teaser */
export function teaserText(blocks, max = 220) {
  const p = blocks.find((b) => b.type === 'paragraph')
  if (!p) return ''
  return p.text.length > max ? p.text.slice(0, max).trimEnd() + '…' : p.text
}

const PROSE_COLLAPSE_COUNT = 4

export function ProductDetails({ description }) {
  const [expanded, setExpanded] = useState(false)
  const blocks = parseDescription(description)
  if (!blocks.length) return null

  const prose = blocks.filter((b) => b.type !== 'table')
  const tables = blocks.filter((b) => b.type === 'table')
  const proseShown = expanded ? prose : prose.slice(0, PROSE_COLLAPSE_COUNT)
  const collapsible = prose.length > PROSE_COLLAPSE_COUNT

  const renderBlock = (b, i) => {
    if (b.type === 'heading') {
      return b.highlight ? (
        <h4 key={i} className="mt-1">
          <span className="inline-block rounded-md border border-brand-gold/40 bg-brand-gold/10 px-2.5 py-1 text-xs font-bold uppercase tracking-widest text-brand-navy">
            {b.text}
          </span>
        </h4>
      ) : (
        <h4 key={i} className="mt-1 font-display text-base font-bold text-brand-ink">{b.text}</h4>
      )
    }
    if (b.type === 'hr') return <hr key={i} className="border-brand-slate/10" />
    return <p key={i} className="text-sm leading-relaxed text-brand-slate">{b.text}</p>
  }

  return (
    <section className="mt-14">
      <h2 className="heading-accent font-display text-2xl font-bold text-brand-ink">
        Product details
      </h2>
      <div className={tables.length ? 'mt-6 grid gap-8 lg:grid-cols-3' : 'mt-6'}>
        {/* Narrative column */}
        <div className={`rounded-2xl bg-white p-6 shadow-card sm:p-8 ${tables.length ? 'lg:col-span-2' : ''}`}>
          <div className="space-y-4">
            {proseShown.map(renderBlock)}
          </div>
          {collapsible && (
            <button
              onClick={() => setExpanded((e) => !e)}
              className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-navy hover:text-brand-gold"
            >
              {expanded ? (
                <>Show less <ChevronUp className="h-4 w-4" /></>
              ) : (
                <>Show full description <ChevronDown className="h-4 w-4" /></>
              )}
            </button>
          )}
        </div>

        {/* Specification tables — always fully visible */}
        {tables.length > 0 && (
          <div className="space-y-6">
            {tables.map((b, i) => (
              <div key={i} className="overflow-hidden rounded-2xl bg-white shadow-card">
                <table className="w-full text-sm">
                  <thead className="bg-brand-navy text-white">
                    <tr>
                      {b.table.header.map((h, j) => (
                        <th key={j} className={`px-4 py-2.5 ${j === b.table.header.length - 1 ? 'text-right' : 'text-left'}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {b.table.body.map((row, j) => (
                      <tr key={j} className="border-t border-brand-slate/10">
                        {row.map((cell, k) => (
                          <td key={k} className={`px-4 py-2.5 ${k === row.length - 1 ? 'text-right font-semibold text-brand-navy' : 'text-brand-slate'}`}>
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  )
}
